import type { GameState } from '../model/gamestate/GameState';
import type { ChatChannel } from './chatCommands';

export type ChatTab = 'ALL' | 'MAP' | 'FRIENDS' | 'GUILD' | 'SYSTEM' | 'WHISPER';

export type ChatChannelInfo =
  | { kind: 'SYSTEM' }
  | { kind: 'MAP'; mapId: string }
  | { kind: 'FRIEND' }
  | { kind: 'GUILD'; guildId: string }
  | { kind: 'WHISPER'; targetId: string };

export interface ChatTabConfig {
    id: ChatTab;
    label: string;
    filter: (info: ChatChannelInfo) => boolean;
    getSendChannel: (ctx: { gameState: GameState; currentGuildName: string | null }) => ChatChannel | null;
    placeholder: string;
}

export function interpretChatChannel(channel: unknown): ChatChannelInfo {
    if (channel && typeof channel === 'object') {
        if ((channel as any).tag === 'System' || (channel as any).tag === 'Global') {
            return { kind: 'SYSTEM' };
        }
        if ((channel as any).tag === 'Map') {
            return { kind: 'MAP', mapId: String((channel as any).value ?? '') };
        }
        if ((channel as any).tag === 'Friend') {
            return { kind: 'FRIEND' };
        }
        if ((channel as any).tag === 'Guild') {
            return { kind: 'GUILD', guildId: String((channel as any).value ?? '') };
        }
        if ((channel as any).tag === 'Whisper') {
            return { kind: 'WHISPER', targetId: String((channel as any).value ?? '') };
        }
        if (Object.prototype.hasOwnProperty.call(channel, 'Map')) {
            return { kind: 'MAP', mapId: String((channel as any).Map ?? '') };
        }
        if (Object.prototype.hasOwnProperty.call(channel, 'Friend')) {
            return { kind: 'FRIEND' };
        }
        if (Object.prototype.hasOwnProperty.call(channel, 'Guild')) {
            return { kind: 'GUILD', guildId: String((channel as any).Guild ?? '') };
        }
        if (Object.prototype.hasOwnProperty.call(channel, 'Whisper')) {
            return { kind: 'WHISPER', targetId: String((channel as any).Whisper ?? '') };
        }
        if (Object.prototype.hasOwnProperty.call(channel, 'Global') || Object.prototype.hasOwnProperty.call(channel, 'System')) {
            return { kind: 'SYSTEM' };
        }
    }
    return { kind: 'SYSTEM' };
}

export function buildChatTabConfigs(t: (key: string) => string): Record<ChatTab, ChatTabConfig> {
    return {
        ALL: {
            id: 'ALL',
            label: t('chat.tabs.all'),
            filter: () => true,
            getSendChannel: ({ gameState }) => {
                const mapId = gameState.getCurrentMapId();
                return mapId ? { tag: 'Map', value: mapId } : null;
            },
            placeholder: t('chat.placeholders.map'),
        },
        MAP: {
            id: 'MAP',
            label: t('chat.tabs.map'),
            filter: (info) => info.kind === 'MAP',
            getSendChannel: ({ gameState }) => {
                const mapId = gameState.getCurrentMapId();
                return mapId ? { tag: 'Map', value: mapId } : null;
            },
            placeholder: t('chat.placeholders.map'),
        },
        FRIENDS: {
            id: 'FRIENDS',
            label: t('chat.tabs.friends'),
            filter: (info) => info.kind === 'FRIEND' || info.kind === 'WHISPER',
            getSendChannel: () => ({ tag: 'Friend' }),
            placeholder: t('chat.placeholders.friends'),
        },
        GUILD: {
            id: 'GUILD',
            label: t('chat.tabs.guild'),
            filter: (info) => info.kind === 'GUILD',
            getSendChannel: ({ currentGuildName }) => currentGuildName ? { tag: 'Guild', value: currentGuildName } : null,
            placeholder: t('chat.placeholders.guild'),
        },
        WHISPER: {
            id: 'WHISPER',
            label: t('chat.tabs.whisper'),
            filter: (info) => info.kind === 'WHISPER',
            getSendChannel: () => null,
            placeholder: t('chat.placeholders.whisper'),
        },
        SYSTEM: {
            id: 'SYSTEM',
            label: t('chat.tabs.system'),
            filter: (info) => info.kind === 'SYSTEM',
            getSendChannel: () => null,
            placeholder: t('chat.placeholders.system'),
        },
    };
}

